import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  LinearProgress,
  Snackbar,
  TablePagination,
  InputAdornment,
  Collapse,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchTransactions, createTransaction, updateTransaction, deleteTransaction } from '../store/transactionsSlice';
import { fetchCategories } from '../store/categoriesSlice';
import { CreateTransactionRequest, UpdateTransactionRequest } from '../types';
import { transactionsApi } from '../services/api';

const currencies = ['CHF', 'USD', 'EUR', 'GBP'];

export default function Transactions() {
  const dispatch = useAppDispatch();
  const { transactions } = useAppSelector((state) => state.transactions);
  const { categories } = useAppSelector((state) => state.categories);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState<Date | null>(null);
  const [filterDateTo, setFilterDateTo] = useState<Date | null>(null);
  const [sortField, setSortField] = useState<'transaction_date' | 'description' | 'amount' | 'category'>('transaction_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    currency: 'CHF',
    transaction_date: new Date(),
    category_id: '',
  });

  useEffect(() => {
    dispatch(fetchTransactions());
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleOpenDialog = (transaction?: any) => {
    if (transaction) {
      setEditingTransaction(transaction);
      setFormData({
        description: transaction.description,
        amount: transaction.amount.toString(),
        currency: transaction.currency,
        transaction_date: new Date(transaction.transaction_date),
        category_id: transaction.category_id?.toString() || '',
      });
    } else {
      setEditingTransaction(null);
      setFormData({
        description: '',
        amount: '',
        currency: 'CHF',
        transaction_date: new Date(),
        category_id: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTransaction(null);
  };

  const handleSubmit = async () => {
    const data = {
      description: formData.description,
      amount: parseFloat(formData.amount),
      currency: formData.currency,
      transaction_date: formData.transaction_date.toISOString().split('T')[0],
      category_id: formData.category_id ? parseInt(formData.category_id) : undefined,
    };

    if (editingTransaction) {
      await dispatch(updateTransaction({ id: editingTransaction.id, data: data as UpdateTransactionRequest }));
    } else {
      await dispatch(createTransaction(data as CreateTransactionRequest));
    }
    
    handleCloseDialog();
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      await dispatch(deleteTransaction(id));
    }
  };

  const getCategoryName = (categoryId?: number) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const handleSort = (field: 'transaction_date' | 'description' | 'amount' | 'category') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(0); // Reset to first page when sorting
  };

  const getSortedTransactions = (transactions: any[]) => {
    return [...transactions].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'transaction_date':
          aValue = new Date(a.transaction_date).getTime();
          bValue = new Date(b.transaction_date).getTime();
          break;
        case 'description':
          aValue = a.description.toLowerCase();
          bValue = b.description.toLowerCase();
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        case 'category':
          aValue = getCategoryName(a.category_id).toLowerCase();
          bValue = getCategoryName(b.category_id).toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (field: 'transaction_date' | 'description' | 'amount' | 'category') => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const result = await transactionsApi.uploadCSV(file);
      setUploadResult(result);
      setSnackbarOpen(true);
      dispatch(fetchTransactions()); // Refresh transactions
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Upload failed. Please try again.';
      alert(`Upload failed: ${errorMessage}`);
    } finally {
      setUploading(false);
      setCsvDialogOpen(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter transactions
  const filteredTransactions = transactions.filter((transaction) => {
    const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || transaction.category_id?.toString() === filterCategory;
    const matchesCurrency = !filterCurrency || transaction.currency === filterCurrency;
    
    const transactionDate = new Date(transaction.transaction_date);
    const matchesDateFrom = !filterDateFrom || transactionDate >= filterDateFrom;
    const matchesDateTo = !filterDateTo || transactionDate <= filterDateTo;
    
    return matchesSearch && matchesCategory && matchesCurrency && matchesDateFrom && matchesDateTo;
  });

  // Sort and paginate transactions
  const sortedTransactions = getSortedTransactions(filteredTransactions);
  const paginatedTransactions = sortedTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const clearFilters = () => {
    setSearchTerm('');
    setFilterCategory('');
    setFilterCurrency('');
    setFilterDateFrom(null);
    setFilterDateTo(null);
    setPage(0);
  };

  const hasActiveFilters = searchTerm || filterCategory || filterCurrency || filterDateFrom || filterDateTo;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Transactions</Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            color={hasActiveFilters ? 'primary' : 'inherit'}
          >
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              {hasActiveFilters ? 'Filters Active' : 'Filters'}
            </Box>
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setCsvDialogOpen(true)}
            sx={{ display: { xs: 'none', md: 'flex' } }}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Add Transaction</Box>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Add</Box>
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Box mb={2}>
        <TextField
          fullWidth
          placeholder="Search transactions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchTerm('')} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        
        <Collapse in={showFilters}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filters
                {hasActiveFilters && (
                  <Button 
                    size="small" 
                    onClick={clearFilters} 
                    startIcon={<ClearIcon />}
                    sx={{ ml: 2 }}
                  >
                    Clear All
                  </Button>
                )}
              </Typography>
              <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filterCategory}
                    label="Category"
                    onChange={(e) => setFilterCategory(e.target.value)}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id.toString()}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={filterCurrency}
                    label="Currency"
                    onChange={(e) => setFilterCurrency(e.target.value)}
                  >
                    <MenuItem value="">All Currencies</MenuItem>
                    {currencies.map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <DatePicker
                  label="From Date"
                  value={filterDateFrom}
                  onChange={(date) => setFilterDateFrom(date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                
                <DatePicker
                  label="To Date"
                  value={filterDateTo}
                  onChange={(date) => setFilterDateTo(date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Stack>
            </CardContent>
          </Card>
        </Collapse>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('transaction_date')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Date
                  {getSortIcon('transaction_date')}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('description')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Description
                  {getSortIcon('description')}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('amount')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Amount
                  {getSortIcon('amount')}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('category')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Category
                  {getSortIcon('category')}
                </Box>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTransactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>
                  {new Date(transaction.transaction_date).toLocaleDateString()}
                </TableCell>
                <TableCell>{transaction.description}</TableCell>
                <TableCell>
                  {transaction.currency} {transaction.amount.toFixed(2)}
                  {transaction.currency !== 'CHF' && (
                    <div style={{ fontSize: '0.8em', color: 'gray' }}>
                      CHF {transaction.amount_chf.toFixed(2)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getCategoryName(transaction.category_id)}
                    size="small"
                    color={transaction.category_id ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(transaction)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(transaction.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={sortedTransactions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count} ${sortedTransactions.length !== transactions.length ? `(filtered from ${transactions.length})` : ''}`
          }
        />
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTransaction ? 'Edit Transaction' : 'Add Transaction'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
              />
              <TextField
                fullWidth
                select
                label="Currency"
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              >
                {currencies.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DatePicker
                label="Transaction Date"
                value={formData.transaction_date}
                onChange={(date) => setFormData({ ...formData, transaction_date: date || new Date() })}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TextField
                fullWidth
                select
                label="Category"
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id.toString()}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTransaction ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Transactions from CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Upload a CSV file with columns: Date, Description, Amount, Currency, Category, Amount in CHF
            </Typography>
            {uploading && <LinearProgress />}
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ marginTop: '16px' }}
              disabled={uploading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Result Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={uploadResult?.failed_imports === 0 ? "success" : "warning"}
        >
          {uploadResult && (
            <>
              Imported {uploadResult.successful_imports}/{uploadResult.total_rows} transactions
              {uploadResult.failed_imports > 0 && (
                <div style={{ marginTop: '8px', fontSize: '0.9em' }}>
                  {uploadResult.errors.slice(0, 3).map((error: string, idx: number) => (
                    <div key={idx}>• {error}</div>
                  ))}
                  {uploadResult.errors.length > 3 && (
                    <div>... and {uploadResult.errors.length - 3} more errors</div>
                  )}
                </div>
              )}
            </>
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
}